// Web Audio synth — generates a heavy cast-iron 45lb plate slam.
// No external assets. Plays only on user interaction (modal mount after a
// "Finish session" click), satisfying autoplay policies. Silently no-ops if
// Web Audio is unavailable or the tab is muted.
//
// Anatomy of the sound (in the order the ear perceives it):
//   T+0ms    Initial broadband CRACK   — wide-spectrum noise burst (the
//                                        instant the cast iron faces meet)
//   T+0ms    Sub THUMP                 — 80 Hz → 32 Hz body resonance (the
//                                        mass of the plate coupling to floor)
//   T+0ms    Inharmonic RING           — 7 partials at cast-iron ratios,
//                                        slight downward pitch droop from
//                                        material damping
//   T+35ms   Settling RATTLE           — secondary noise burst, the plate
//                                        rocking on the collar
//   master → soft saturation → compressor → convolver wet bus → destination
//
// Tuned to feel meaty on phone speakers (lots of 100–250 Hz body) AND on
// headphones (clean sub down to 30 Hz, crisp transient).

let ctxRef = null;
let impulseRef = null;

function getCtx() {
    if (typeof window === "undefined") return null;
    if (!ctxRef) {
        const C = window.AudioContext || window.webkitAudioContext;
        if (!C) return null;
        try { ctxRef = new C(); } catch (e) { return null; }
    }
    return ctxRef;
}

// Build a short, dense impulse response that imitates a concrete gym room:
// tight, slappy, ~280ms tail. Generated once and reused.
function getImpulse(ctx) {
    if (impulseRef) return impulseRef;
    const seconds = 0.28;
    const rate = ctx.sampleRate;
    const length = Math.floor(seconds * rate);
    const buf = ctx.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
        const data = buf.getChannelData(ch);
        for (let i = 0; i < length; i++) {
            const t = i / length;
            // Dense noise with exponential decay; slight stereo decorrelation.
            const decay = Math.pow(1 - t, 3.2);
            data[i] = (Math.random() * 2 - 1) * decay * (ch === 0 ? 1 : 0.92);
        }
    }
    impulseRef = buf;
    return buf;
}

// Soft-clip curve for the saturation stage — adds harmonic warmth and stops
// the transient from clipping the DAC.
function makeSaturationCurve(amount = 1.4) {
    const n = 1024;
    const curve = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const x = (i * 2) / n - 1;
        curve[i] = Math.tanh(x * amount);
    }
    return curve;
}

export function playPlateClang() {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;

    // ── MASTER CHAIN ─────────────────────────────────────────────────────
    // dry → saturate → compressor → destination
    // dry also → convolver → wet gain → compressor (parallel wet bus)
    const saturate = ctx.createWaveShaper();
    saturate.curve = makeSaturationCurve(1.6);
    saturate.oversample = "4x";

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.setValueAtTime(-14, now);
    comp.knee.setValueAtTime(8, now);
    comp.ratio.setValueAtTime(4, now);
    comp.attack.setValueAtTime(0.002, now);
    comp.release.setValueAtTime(0.18, now);

    const master = ctx.createGain();
    master.gain.value = 0.9;

    saturate.connect(comp).connect(master).connect(ctx.destination);

    // Parallel reverb send for room/space.
    const convolver = ctx.createConvolver();
    convolver.buffer = getImpulse(ctx);
    const wet = ctx.createGain();
    wet.gain.value = 0.28;
    convolver.connect(wet).connect(saturate);

    // A single "bus" that everything below connects into; from here it
    // splits dry into `saturate` and wet into `convolver`.
    const bus = ctx.createGain();
    bus.gain.value = 1.0;
    bus.connect(saturate);
    bus.connect(convolver);

    // ── 1) INITIAL CRACK — broadband transient ───────────────────────────
    // Two overlapping noise bursts give the impact a "two-faced" double-tap
    // feel (the moment the rims meet, then the bodies follow).
    const makeNoiseBurst = (duration, peak, filterType, filterFreq, filterQ, startOffset = 0) => {
        const len = Math.floor(ctx.sampleRate * duration);
        const buf = ctx.createBuffer(1, len, ctx.sampleRate);
        const data = buf.getChannelData(0);
        // Sharp attack, very fast exponential decay → percussive transient.
        for (let i = 0; i < len; i++) {
            const env = Math.exp(-i / (ctx.sampleRate * 0.008));
            data[i] = (Math.random() * 2 - 1) * env;
        }
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const f = ctx.createBiquadFilter();
        f.type = filterType;
        f.frequency.value = filterFreq;
        f.Q.value = filterQ;
        const g = ctx.createGain();
        g.gain.setValueAtTime(peak, now + startOffset);
        g.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
        src.connect(f).connect(g).connect(bus);
        src.start(now + startOffset);
        src.stop(now + startOffset + duration + 0.02);
    };

    // The CRACK — the loud high-mid edge of the impact.
    makeNoiseBurst(0.06, 0.95, "bandpass", 2400, 0.7, 0);
    // The CLACK — slightly delayed, lower, the bodies seating against each other.
    makeNoiseBurst(0.05, 0.7, "lowpass", 900, 0.9, 0.003);

    // ── 2) SUB THUMP — deep low-end body ────────────────────────────────
    // Two stacked sub layers for weight on both phone speakers (which can't
    // reproduce sub-bass but DO feel the harmonics) and on headphones.
    const subA = ctx.createOscillator();
    const subAGain = ctx.createGain();
    subA.type = "sine";
    subA.frequency.setValueAtTime(95, now);
    subA.frequency.exponentialRampToValueAtTime(32, now + 0.22);
    subAGain.gain.setValueAtTime(1.0, now);
    subAGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
    subA.connect(subAGain).connect(bus);
    subA.start(now);
    subA.stop(now + 0.6);

    // A second sub one octave down for headphone "punch" — perceived weight.
    const subB = ctx.createOscillator();
    const subBGain = ctx.createGain();
    subB.type = "sine";
    subB.frequency.setValueAtTime(58, now);
    subB.frequency.exponentialRampToValueAtTime(28, now + 0.35);
    subBGain.gain.setValueAtTime(0.55, now);
    subBGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.75);
    subB.connect(subBGain).connect(bus);
    subB.start(now);
    subB.stop(now + 0.8);

    // ── 3) INHARMONIC CAST-IRON RING ────────────────────────────────────
    // Cast iron is inharmonic — partials sit at non-integer multiples of
    // the fundamental. These ratios are tuned by ear to imitate two 45lb
    // plates touching face-to-face (not a clean bell — a dull, woody ring).
    // Each partial droops slightly in pitch as the material self-damps.
    const fundamental = 360;
    const partialRatios = [1.0, 1.84, 2.71, 3.93, 5.42, 7.16, 9.21];
    partialRatios.forEach((ratio, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        // Triangle has fewer high harmonics than saw → woodier ring.
        osc.type = i < 3 ? "triangle" : "sine";
        const f0 = fundamental * ratio;
        // Tiny random detune so the partials don't sound perfectly digital.
        const detune = (Math.random() - 0.5) * 12; // cents
        osc.detune.setValueAtTime(detune, now);
        osc.frequency.setValueAtTime(f0, now);
        // Slight downward pitch droop — material friction loss.
        osc.frequency.exponentialRampToValueAtTime(f0 * 0.93, now + 0.9);

        // Lower partials are louder; upper ones are quick metallic shimmer.
        const peak = 0.18 * Math.pow(0.72, i);
        const decay = i < 3 ? 0.55 : 0.32;
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(peak, now + 0.003); // very fast attack
        g.gain.exponentialRampToValueAtTime(0.0001, now + decay);
        osc.connect(g).connect(bus);
        osc.start(now);
        osc.stop(now + decay + 0.05);
    });

    // ── 4) BODY THUMP at mid-low (the plate "thunk", not a pure sine) ──
    // Filtered noise gives the impact body — the wood-ish "thock" beneath
    // the metal ring. This is what makes it feel HEAVY rather than tinny.
    const bodyLen = Math.floor(ctx.sampleRate * 0.18);
    const bodyBuf = ctx.createBuffer(1, bodyLen, ctx.sampleRate);
    const bodyData = bodyBuf.getChannelData(0);
    for (let i = 0; i < bodyLen; i++) {
        bodyData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.025));
    }
    const bodySrc = ctx.createBufferSource();
    bodySrc.buffer = bodyBuf;
    const bodyFilter = ctx.createBiquadFilter();
    bodyFilter.type = "bandpass";
    bodyFilter.frequency.setValueAtTime(180, now);
    bodyFilter.frequency.exponentialRampToValueAtTime(110, now + 0.18);
    bodyFilter.Q.value = 4.5;
    const bodyGain = ctx.createGain();
    bodyGain.gain.setValueAtTime(0.7, now);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    bodySrc.connect(bodyFilter).connect(bodyGain).connect(bus);
    bodySrc.start(now);

    // ── 5) SETTLING RATTLE — the plate rocking back onto the collar ────
    // Two faint, brief high-mid bursts 35ms and 70ms after impact. Adds
    // realism — real plate slams aren't a single sample.
    makeNoiseBurst(0.04, 0.18, "bandpass", 3600, 1.2, 0.035);
    makeNoiseBurst(0.03, 0.12, "bandpass", 4400, 1.4, 0.072);
}
